import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import '../styles/People.css';

// "Shop for Others" management page. You connect with people by username;
// once they accept, the extension can recommend THEIR size while you shop —
// without ever showing you their measurements.
function People() {
  const [people, setPeople] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [username, setUsername] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState(null);

  const myUsername = localStorage.getItem('username');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadConnections = useCallback(async () => {
    try {
      const data = await apiFetch('/connections');
      setPeople(data.people || []);
      setIncoming(data.incoming || []);
      setOutgoing(data.outgoing || []);
    } catch (err) {
      showToast(err.message || 'Could not load your people', 'error');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const handleSendRequest = async (e) => {
    e.preventDefault();
    const target = username.trim().toLowerCase();
    if (!target) return;

    setSending(true);
    try {
      const data = await apiFetch('/connections/request', {
        method: 'POST',
        body: JSON.stringify({ username: target }),
      });
      showToast(data.message);
      setUsername('');
      await loadConnections();
    } catch (err) {
      showToast(err.message || 'Could not send request', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleAccept = async (connectionId) => {
    try {
      const data = await apiFetch(`/connections/${connectionId}/accept`, { method: 'POST' });
      showToast(data.message);
      await loadConnections();
    } catch (err) {
      showToast(err.message || 'Could not accept request', 'error');
    }
  };

  const handleRemove = async (connectionId, label) => {
    try {
      await apiFetch(`/connections/${connectionId}`, { method: 'DELETE' });
      showToast(label);
      await loadConnections();
    } catch (err) {
      showToast(err.message || 'Could not remove', 'error');
    }
  };

  return (
    <div className="people-page">
      <div className="people-container">
        <div className="people-header">
          <h1>Shop for Others</h1>
          <p className="people-subtitle">
            Connect with family and friends so you can buy clothes that fit them —
            without ever needing to know their measurements. While you shop, the
            Tailor extension can show the right size for anyone in your circle.
            They only share their <em>size</em>, never their measurements.
          </p>
          {myUsername && (
            <p className="people-my-username">
              Your username: <strong>@{myUsername}</strong> — share it so people can add you.
            </p>
          )}
        </div>

        {/* Add someone */}
        <section className="people-section">
          <h2>Add someone</h2>
          <p className="people-section-desc">
            Enter their Tailor username. They'll need to accept your request before
            you can see size recommendations for them.
          </p>
          <form className="people-add-form" onSubmit={handleSendRequest}>
            <div className="people-add-input-wrapper">
              <span className="people-at">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                placeholder="their_username"
                maxLength={20}
                aria-label="Username to add"
              />
            </div>
            <button type="submit" className="btn btn-primary people-add-btn" disabled={sending || !username}>
              {sending ? 'Sending…' : 'Send Request'}
            </button>
          </form>
        </section>

        {/* Incoming requests */}
        {incoming.length > 0 && (
          <section className="people-section">
            <h2>Requests for you</h2>
            <p className="people-section-desc">
              These people want to be able to shop for your size. Accepting shares your
              recommended size with them — never your measurements.
            </p>
            <ul className="people-list">
              {incoming.map((req) => (
                <li className="people-item" key={req.connectionId}>
                  <span className="people-name">@{req.username}</span>
                  <div className="people-item-actions">
                    <button className="btn-small btn-small-primary" onClick={() => handleAccept(req.connectionId)}>
                      Accept
                    </button>
                    <button
                      className="btn-small btn-small-ghost"
                      onClick={() => handleRemove(req.connectionId, `Declined @${req.username}'s request`)}
                    >
                      Decline
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Your people */}
        <section className="people-section">
          <h2>Your people</h2>
          {loaded && people.length === 0 ? (
            <p className="people-empty">
              No one yet. Add someone above — once they accept, their name will appear
              in the size popup while you shop.
            </p>
          ) : (
            <ul className="people-list">
              {people.map((person) => (
                <li className="people-item" key={person.connectionId}>
                  <div>
                    <span className="people-name">@{person.username}</span>
                    <span className="people-since">
                      connected {new Date(person.since).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="people-item-actions">
                    <button
                      className="btn-small btn-small-ghost"
                      onClick={() => {
                        if (window.confirm(`Remove @${person.username}? Neither of you will be able to shop for the other any more.`)) {
                          handleRemove(person.connectionId, `Removed @${person.username}`);
                        }
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Outgoing requests */}
        {outgoing.length > 0 && (
          <section className="people-section">
            <h2>Waiting for a reply</h2>
            <ul className="people-list">
              {outgoing.map((req) => (
                <li className="people-item" key={req.connectionId}>
                  <div>
                    <span className="people-name">@{req.username}</span>
                    <span className="people-since">request pending</span>
                  </div>
                  <div className="people-item-actions">
                    <button
                      className="btn-small btn-small-ghost"
                      onClick={() => handleRemove(req.connectionId, `Cancelled request to @${req.username}`)}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* How it works */}
        <div className="people-tips">
          <h3>How shopping for others works</h3>
          <ul>
            <li>Both of you need a Tailor account with measurements saved</li>
            <li>Send a request to their username; they accept it from this page</li>
            <li>While shopping, open the size popup and pick their name to see their recommended size</li>
            <li>You'll only ever see their recommended size and fit — never their measurements</li>
            <li>Either of you can remove the connection at any time</li>
          </ul>
        </div>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default People;
